import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import * as React from "react";
import { LoadingSpinner } from "source/app/presentation/MainPanel/Calendar/LoadingSpinner";
import { SearchBookOfMindNodesAsync } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/BookOfMindNodeIndex";
import { SetBookOfMindState } from "../../../Helpers/BookOfMindHelpers";
import { IBookOfMindState } from "../../../Helpers/IBookOfMindState";
import * as toastr from "toastr";
import * as styles from "./AddReadingListNode.css";
import { GetNonOverlappingReadingListAsync } from "./GetNonOverlappingReadingListAsync";

const minimumSearchLength = 2;

interface IReadingListSearchResult {
    node: IWorkflowNode;
    containingNodeName?: string;
}

export function AddReadingListNode({
    state,
}: {
    state: IBookOfMindState;
}) {
    const [searchText, setSearchText] = React.useState("");
    const [results, setResults] = React.useState<
        IReadingListSearchResult[]
    >([]);
    const [selectedNode, setSelectedNode] =
        React.useState<IWorkflowNode | null>(null);
    const [isAdding, setIsAdding] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchMessage, setSearchMessage] = React.useState("");

    React.useEffect(() => {
        const query = searchText.trim();
        const isNodeId = /^#?\d+$/.test(query);

        if (
            selectedNode?.name === searchText ||
            (query.length < minimumSearchLength && !isNodeId)
        ) {
            setResults([]);
            setIsSearching(false);
            setSearchMessage("");
            return;
        }

        let isCurrent = true;
        setIsSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const searchResult =
                    await SearchBookOfMindNodesAsync(
                        query,
                        state.readingListNodeNames
                    );

                if (isCurrent) {
                    setResults(
                        searchResult.results
                            .sort(
                                (left, right) =>
                                    left.node.name.length -
                                        right.node.name.length ||
                                    left.node.name.localeCompare(
                                        right.node.name
                                    )
                            )
                            .slice(0, 8)
                    );
                    setSearchMessage(
                        searchResult.results.length === 0
                            ? "No Book of Mind nodes found"
                            : ""
                    );
                }
            } finally {
                if (isCurrent) {
                    setIsSearching(false);
                }
            }
        }, 250);

        return () => {
            isCurrent = false;
            window.clearTimeout(timer);
        };
    }, [searchText, selectedNode, state.readingListNodeNames]);

    const addSelectedNode = async () => {
        if (!selectedNode) {
            return;
        }

        setIsAdding(true);

        try {
            const result = await GetNonOverlappingReadingListAsync(
                selectedNode,
                state.readingListNodeNames
            );

            if (result.containingNodeName) {
                toastr.info(
                    `"${selectedNode.name}" is already included under ` +
                        `"${result.containingNodeName}"`
                );
            } else {
                SetBookOfMindState({
                    readingListNodeNames: result.nodeNames,
                });
            }

            setSearchText("");
            setSelectedNode(null);
            setResults([]);
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchRow}>
                <input
                    type="search"
                    value={searchText}
                    placeholder="Add node by name or #ID..."
                    autoComplete="off"
                    className={styles.searchInput}
                    onChange={(event) => {
                        setSearchText(event.target.value);
                        setSelectedNode(null);
                        setSearchMessage("");
                    }}
                />
                <button
                    type="button"
                    className={styles.addButton}
                    disabled={!selectedNode || isAdding}
                    onClick={addSelectedNode}
                >
                    + Add
                </button>
            </div>
            {isSearching ? (
                <LoadingSpinner className={styles.spinner} />
            ) : null}
            {!isSearching && searchMessage ? (
                <div className={styles.searchMessage}>
                    {searchMessage}
                </div>
            ) : null}
            {results.length > 0 ? (
                <div className={styles.results}>
                    {results.map(({ node, containingNodeName }) => (
                        <button
                            type="button"
                            key={node.id}
                            className={styles.result}
                            disabled={containingNodeName !== undefined}
                            onClick={() => {
                                setSelectedNode(node);
                                setSearchText(node.name);
                                setResults([]);
                            }}
                        >
                            <span>{node.name}</span>
                            <small>
                                #{node.id}
                                {containingNodeName
                                    ? ` · Included via ${containingNodeName}`
                                    : ""}
                            </small>
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
